import type { ScalabilityConfiguration, ScaleRule } from '@/api/types'
import type { Target } from './target'

export type RuleKind = ScaleRule['kind']
export type Operator = NonNullable<ScaleRule['operator']>
export type ScaleAction = NonNullable<ScaleRule['action']>
export type ActionMode = 'by' | 'to'

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export const TIME_ZONES = ['Europe/Paris', 'UTC']

const TIME_PATTERN = /^\d{1,2}:\d{2}(:\d{2})?$/

/** A rule as edited: every field of both kinds is kept so switching kind does not lose input; numbers stay text. */
export interface RuleDraft {
  key: number
  kind: RuleKind
  name: string
  instances: string
  queues: string[]
  messageCountPerMachine: string
  operator: Operator
  action: ScaleAction
  days: string[]
  from: string
  to: string
  timeZone: string
}

export interface ConfigurationDraft {
  isActive: boolean
  minInstances: string
  maxInstances: string
  rules: RuleDraft[]
}

let nextKey = 0

/** Scaling out reads ">=" and increases; scaling in reads "<=" and decreases. */
export const actionFor = (operator: Operator, mode: ActionMode): ScaleAction => `${operator === '>=' ? 'increase' : 'decrease'}${mode}`
export const actionMode = (action: ScaleAction): ActionMode => (action.endsWith('to') ? 'to' : 'by')

export function newRule(kind: RuleKind = 'Metric'): RuleDraft {
  return {
    key: nextKey++,
    kind,
    name: '',
    instances: '1',
    queues: [''],
    messageCountPerMachine: '',
    operator: '>=',
    action: 'increaseby',
    days: [],
    from: '',
    to: '',
    timeZone: TIME_ZONES[0],
  }
}

export function defaultConfiguration(service: string, { product }: Target): ScalabilityConfiguration {
  return { service, subscription: product.toLowerCase(), isActive: true, minInstances: 0, maxInstances: 0, rules: [] }
}

function toRuleDraft(rule: ScaleRule): RuleDraft {
  const base = newRule(rule.kind)
  const operator = rule.operator ?? base.operator
  return {
    ...base,
    name: rule.name ?? '',
    instances: String(rule.instances ?? 1),
    queues: rule.queues?.length ? rule.queues : [''],
    messageCountPerMachine: rule.messageCountPerMachine === undefined ? '' : String(rule.messageCountPerMachine),
    operator,
    action: rule.action ?? actionFor(operator, 'by'),
    days: rule.days ?? [],
    from: rule.from ?? '',
    to: rule.to ?? '',
    timeZone: rule.timeZone ?? base.timeZone,
  }
}

export function toDraft(configuration: ScalabilityConfiguration): ConfigurationDraft {
  return {
    isActive: configuration.isActive,
    minInstances: String(configuration.minInstances),
    maxInstances: String(configuration.maxInstances),
    rules: configuration.rules.map(toRuleDraft),
  }
}

const isWhole = (value: string) => value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) >= 0
const ruleName = (rule: RuleDraft, index: number) => rule.name.trim() || `Rule ${index + 1}`

/** Returns the first problem found, or null when the draft can be saved. */
export function validate(draft: ConfigurationDraft): string | null {
  if (!isWhole(draft.minInstances) || !isWhole(draft.maxInstances)) return 'Minimum and maximum instances must be whole numbers of 0 or more.'
  if (Number(draft.minInstances) > Number(draft.maxInstances)) return 'The minimum number of instances cannot exceed the maximum.'
  for (const [index, rule] of draft.rules.entries()) {
    const label = ruleName(rule, index)
    if (rule.kind === 'Metric') {
      if (!rule.queues.some((queue) => queue.trim())) return `${label}: add at least one queue name.`
      if (!isWhole(rule.instances) || Number(rule.instances) < 1) return `${label}: instances must be a whole number of 1 or more.`
      if (!isWhole(rule.messageCountPerMachine)) return `${label}: the message count per machine must be a whole number of 0 or more.`
    } else {
      if (!isWhole(rule.instances)) return `${label}: instances must be a whole number of 0 or more.`
      if ((rule.from.trim() && !TIME_PATTERN.test(rule.from.trim())) || (rule.to.trim() && !TIME_PATTERN.test(rule.to.trim()))) {
        return `${label}: times must look like 00:00:00.`
      }
    }
  }
  return null
}

/** Builds the payload; fields that do not belong to a rule's kind are left out. */
function toRule(rule: RuleDraft, index: number): ScaleRule {
  const base = { name: ruleName(rule, index), instances: Number(rule.instances) }
  if (rule.kind === 'Metric') {
    return {
      ...base,
      kind: 'Metric',
      queues: rule.queues.map((queue) => queue.trim()).filter(Boolean),
      messageCountPerMachine: Number(rule.messageCountPerMachine),
      operator: rule.operator,
      action: rule.action,
    }
  }
  return {
    ...base,
    kind: 'Schedule',
    days: DAYS.filter((day) => rule.days.includes(day)),
    from: rule.from.trim() || undefined,
    to: rule.to.trim() || undefined,
    timeZone: rule.timeZone,
  }
}

export function toConfiguration(draft: ConfigurationDraft, base: ScalabilityConfiguration): ScalabilityConfiguration {
  return {
    service: base.service,
    subscription: base.subscription,
    isActive: draft.isActive,
    minInstances: Number(draft.minInstances),
    maxInstances: Number(draft.maxInstances),
    rules: draft.rules.map(toRule),
  }
}
